import React, { useMemo, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { DropDownList, MultiSelect } from "@progress/kendo-react-dropdowns";
import Scrollable from "../Shared/Edit/Scrollable";
import { useTranslation } from "react-i18next";

const DEFAULT_ORDER_VALUE = 10000000000;
const OPERATION_OPTIONS = [
    { text: "Read & Edit", value: "Read & Edit" },
    { text: "Read Only", value: "Read Only" }
];

const getUuid = () => {
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        const v = c === "x" ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

const normalizeRuleForComparison = (rule) => {
    return {
        stateId: rule.stateId || "",
        operation: rule.operation || "",
        roleIds: [...(rule.roleIds || [])].sort()
    };
};

const normalizeReasons = (reasons) => {
    return (reasons || []).map(reason => reason.trim()).filter(reason => reason.length > 0);
};

const getEmptyRule = (order = DEFAULT_ORDER_VALUE) => ({
    _id: getUuid(),
    stateId: "",
    stateName: "",
    operation: "Read Only",
    roleIds: [],
    order
});

const LifecycleRulesDialog = ({
    onClose,
    onSave,
    states,
    stateAttributeName,
    roles,
    lifecycleRules,
    completeStateId,
    overrideRoleIds,
    overrideReasons
}) => {
    const { t } = useTranslation();
    const [rules, setRules] = useState((lifecycleRules && lifecycleRules.length > 0) ? lifecycleRules : [getEmptyRule()]);
    const [selectedCompleteStateId, setSelectedCompleteStateId] = useState(completeStateId || "");
    const [selectedOverrideRoleIds, setSelectedOverrideRoleIds] = useState(overrideRoleIds || []);
    const [reasons, setReasons] = useState(overrideReasons?.length ? overrideReasons : [""]);

    const defaultValues = useMemo(() => ({
        rules: lifecycleRules || [],
        completeStateId: completeStateId || "",
        overrideRoleIds: overrideRoleIds || [],
        overrideReasons: overrideReasons || []
    }), [lifecycleRules, completeStateId, overrideRoleIds, overrideReasons]);

    const rolesSorted = useMemo(() => {
        return [...(roles || [])]
            .map(role => ({
                ...role,
                displayRoleName: role.roleName || role.name || role.title || ""
            }))
            .filter(role => role.displayRoleName)
            .sort((a, b) => a.displayRoleName.localeCompare(b.displayRoleName));
    }, [roles]);

    const getStateById = (stateId) => {
        return (states || []).find(state => state._id === stateId) || null;
    };

    const getOperationByValue = (value) => {
        return OPERATION_OPTIONS.find(operation => operation.value === value) || null;
    };

    const getRolesByIds = (roleIds) => {
        return (roleIds || [])
            .map(roleId => rolesSorted.find(role => role._id === roleId))
            .filter(role => role);
    };

    const getAvailableStates = (rule) => {
        const selectedStateIds = rules
            .filter(item => item._id !== rule._id)
            .map(item => item.stateId)
            .filter(Boolean);

        return (states || []).filter(state => {
            return !selectedStateIds.includes(state._id) || state._id === rule.stateId;
        });
    };

    const addRule = () => {
        const nextOrder = rules.length > 0
            ? parseInt(rules[rules.length - 1].order || DEFAULT_ORDER_VALUE, 10) + DEFAULT_ORDER_VALUE
            : DEFAULT_ORDER_VALUE;
        setRules([...rules, getEmptyRule(nextOrder)]);
    };

    const updateRule = (ruleId, key, value) => {
        setRules(prevRules => prevRules.map(rule => {
            if (rule._id !== ruleId) {
                return rule;
            }
            return {
                ...rule,
                [key]: value
            };
        }));
    };

    const removeRule = (ruleId) => {
        setRules(prevRules => {
            const updatedRules = prevRules.filter(rule => rule._id !== ruleId);
            return updatedRules.length > 0 ? updatedRules : [getEmptyRule()];
        });
    };

    const updateReason = (index, value) => {
        setReasons(prevReasons => prevReasons.map((reason, idx) => idx === index ? value : reason));
    };

    const addReason = () => {
        setReasons(prevReasons => [...prevReasons, ""]);
    };

    const removeReason = (index) => {
        setReasons(prevReasons => prevReasons.filter((_, idx) => idx !== index));
    };

    const areRulesChanged = () => {
        if (rules.length !== defaultValues.rules.length) {
            return true;
        }
        for (let index = 0; index < rules.length; index++) {
            const currentRule = normalizeRuleForComparison(rules[index]);
            const defaultRule = normalizeRuleForComparison(defaultValues.rules[index]);
            if (
                currentRule.stateId !== defaultRule.stateId ||
                currentRule.operation !== defaultRule.operation ||
                currentRule.roleIds.length !== defaultRule.roleIds.length ||
                currentRule.roleIds.some((roleId, roleIndex) => roleId !== defaultRule.roleIds[roleIndex])
            ) {
                return true;
            }
        }
        return false;
    };

    const areReasonsChanged = () => {
        const currentReasons = normalizeReasons(reasons);
        const defaultReasons = normalizeReasons(defaultValues.overrideReasons);
        if (currentReasons.length !== defaultReasons.length) {
            return true;
        }
        return currentReasons.some((reason, index) => reason !== defaultReasons[index]);
    };

    const isAnyRuleInvalid = () => {
        return rules.some(rule => !rule.stateId || !rule.operation || !rule.roleIds || rule.roleIds.length === 0);
    };

    const getRuleValidation = (rule) => ({
        stateMissing: !rule.stateId,
        operationMissing: !rule.operation,
        rolesMissing: !rule.roleIds || rule.roleIds.length === 0
    });

    const isSaveDisabled = () => {
        if (isAnyRuleInvalid()) {
            return true;
        }
        const isChanged = (
            areRulesChanged() ||
            selectedCompleteStateId !== defaultValues.completeStateId ||
            [...selectedOverrideRoleIds].sort().join(",") !== [...defaultValues.overrideRoleIds].sort().join(",") ||
            areReasonsChanged()
        );
        return !isChanged;
    };

    const handleSave = () => {
        const mappedRules = rules.map((rule, index) => {
            const state = getStateById(rule.stateId);
            return {
                _id: rule._id,
                stateId: rule.stateId,
                stateName: state?.name || "",
                operation: rule.operation,
                roleIds: [...new Set(rule.roleIds)],
                order: parseInt((index + 1) * DEFAULT_ORDER_VALUE, 10)
            };
        });

        onSave({
            lifecycleRules: mappedRules,
            completeStateId: selectedCompleteStateId || "",
            overrideRoleIds: [...new Set(selectedOverrideRoleIds)],
            overrideReasons: normalizeReasons(reasons)
        });
    };

    return (
        <Dialog
            width={"80%"}
            height={"80%"}
            title={t("entity.property_panel.lifecycle_rules.manage_title")}
            onClose={onClose}
        >
            <Scrollable>
                <div className="my-3">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <div className="form-label text-secondary text-uppercase fw-bold mb-0">
                            {t("entity.property_panel.lifecycle_rules.state_based_rules")}
                        </div>
                        <button
                            type="button"
                            className="btn btn-outline-primary d-inline-flex align-items-center px-3 py-1 fw-semibold"
                            onClick={addRule}
                            disabled={(states || []).length === 0}
                        >
                            <i className="fa fa-plus me-1"></i>
                            {t("entity.property_panel.lifecycle_rules.add_rule")}
                        </button>
                    </div>
                    {!!stateAttributeName && (
                        <div className="alert alert-primary border d-flex align-items-center py-2 px-3 mb-3">
                            <i className="fa fa-circle-info me-2"></i>
                            <span className="fw-semibold">
                                {t("entity.property_panel.lifecycle_rules.selected_state_attribute", { attributeName: stateAttributeName })}
                            </span>
                        </div>
                    )}
                    {(states || []).length === 0 &&
                        <div className="alert alert-light border mb-3">
                            {t("entity.property_panel.lifecycle_rules.no_states")}
                        </div>
                    }
                    {rules.map((rule, index) => {
                        const validation = getRuleValidation(rule);
                        return (
                            <div className="border rounded p-3 mb-3 bg-light" key={rule._id}>
                                <div className="d-flex align-items-center flex-wrap gap-2">
                                    <span className="fw-semibold">{t("entity.property_panel.lifecycle_rules.if_state_is")}</span>
                                    <DropDownList
                                        name="stateId"
                                        textField="name"
                                        dataItemKey="_id"
                                        data={getAvailableStates(rule)}
                                        value={getStateById(rule.stateId)}
                                        className={validation.stateMissing ? "is-invalid" : ""}
                                        disabled={(states || []).length === 0}
                                        style={{ width: 180 }}
                                        onChange={(event) => updateRule(rule._id, "stateId", event.target.value?._id || "")}
                                    />
                                    <span className="fw-semibold">{t("entity.property_panel.lifecycle_rules.then_allow")}</span>
                                    <DropDownList
                                        name="operation"
                                        textField="text"
                                        dataItemKey="value"
                                        data={OPERATION_OPTIONS}
                                        value={getOperationByValue(rule.operation)}
                                        className={validation.operationMissing ? "is-invalid" : ""}
                                        style={{ width: 180 }}
                                        onChange={(event) => updateRule(rule._id, "operation", event.target.value?.value || "")}
                                    />
                                    <span className="fw-semibold">{t("entity.property_panel.lifecycle_rules.by_roles")}</span>
                                    <div style={{ minWidth: 220, flexGrow: 1 }}>
                                        <MultiSelect
                                            name="roleIds"
                                            textField="displayRoleName"
                                            dataItemKey="_id"
                                            data={rolesSorted}
                                            value={getRolesByIds(rule.roleIds)}
                                            className={validation.rolesMissing ? "is-invalid" : ""}
                                            placeholder={t("entity.property_panel.lifecycle_rules.select_role_placeholder")}
                                            onChange={(event) => {
                                                updateRule(rule._id, "roleIds", event.target.value.map(role => role._id));
                                            }}
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-link text-muted p-0 ms-2"
                                        title={t("entity.property_panel.lifecycle_rules.delete_rule")}
                                        onClick={() => removeRule(rule._id)}
                                        disabled={rules.length === 1 && index === 0}
                                    >
                                        <i className="fa fa-trash"></i>
                                    </button>
                                </div>
                                {(validation.stateMissing || validation.operationMissing || validation.rolesMissing) && (
                                    <div className="text-danger mt-2 small">
                                        {t("entity.property_panel.lifecycle_rules.rule_required_error")}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="my-4">
                    <div className="form-label">{t("entity.property_panel.lifecycle_rules.complete_state")}</div>
                    <DropDownList
                        textField="name"
                        dataItemKey="_id"
                        data={states || []}
                        value={getStateById(selectedCompleteStateId)}
                        className="form-control-custom"
                        onChange={(event) => setSelectedCompleteStateId(event.target.value?._id || "")}
                    />
                </div>

                <div className="my-4">
                    <div className="form-label">{t("entity.property_panel.lifecycle_rules.override_roles")}</div>
                    <MultiSelect
                        textField="displayRoleName"
                        dataItemKey="_id"
                        data={rolesSorted}
                        className="form-control-custom"
                        value={getRolesByIds(selectedOverrideRoleIds)}
                        placeholder={t("entity.property_panel.lifecycle_rules.select_role_placeholder")}
                        onChange={(event) => setSelectedOverrideRoleIds(event.target.value.map(role => role._id))}
                    />
                </div>

                <div className="my-4">
                    <div className="form-label">{t("entity.property_panel.lifecycle_rules.override_reasons")}</div>
                    {reasons.map((reason, index) => (
                        <div className="d-flex align-items-center gap-2 mb-2" key={index}>
                            <input
                                type="text"
                                className="form-control"
                                value={reason}
                                placeholder={t("entity.property_panel.lifecycle_rules.reason_placeholder")}
                                onChange={(event) => updateReason(index, event.target.value)}
                            />
                            <i className="fa fa-times pointer" onClick={() => removeReason(index)}></i>
                        </div>
                    ))}
                    <button
                        type="button"
                        className="btn p-2 hover-bg-7 hover-border-0 form-label d-inline-block t-primary pointer"
                        onClick={addReason}
                    >
                        <i className="me-1 fa fa-plus"></i>
                        {t("entity.property_panel.lifecycle_rules.add_reason")}
                    </button>
                </div>
            </Scrollable>
            <DialogActionsBar layout="end">
                <div className="m-3">
                    <button className="btn btn-secondary" onClick={onClose}>
                        {t("entity.property_panel.lifecycle_rules.buttons.cancel")}
                    </button>
                    <button className="btn btn-primary" disabled={isSaveDisabled()} onClick={handleSave}>
                        {t("entity.property_panel.lifecycle_rules.buttons.save")}
                    </button>
                </div>
            </DialogActionsBar>
        </Dialog>
    );
};

export default LifecycleRulesDialog;
